import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const EarlyAccessQueue = () => {
    const user = useSelector((state) => state?.user);
    const [inquiries, setInquiries] = useState([]);
    const [statusFilter, setStatusFilter] = useState("All");

    // Drawer State
    const [selectedInquiry, setSelectedInquiry] = useState(null);
    const [showDrawer, setShowDrawer] = useState(false);
    const [newNote, setNewNote] = useState("");
    const [updatedStatus, setUpdatedStatus] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);
    const [isNoteFocused, setIsNoteFocused] = useState(false);

    const fetchInquiries = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BASE_URL}/provider-inquiry/list`,
                {
                    headers: {
                        Authorization: `Bearer ${user.user?.token}`,
                    },
                }
            );
            if (response.data.success) {
                setInquiries(response.data.data);
            }
        } catch (error) {
            console.error("🚀 -> fetchInquiries -> error:", error);
        }
    };

    useEffect(() => {
        fetchInquiries();
    }, []);

    const statusColors = {
        Scheduled: { bg: "#D0FAE5", color: "#006145" },
        Contacted: { bg: "#D0EEFA", color: "#003F61" },
        Pending: { bg: "#FFFED8", color: "#687000" },
        "No Response": { bg: "#FEE9C6", color: "#973B00" },
        "Not Interested": { bg: "#FEE4E6", color: "#ED3A3A" },
    };

    const handleOpenDrawer = (inquiry) => {
        setSelectedInquiry(inquiry);
        setUpdatedStatus(inquiry.status || "Pending");
        setNewNote("");
        setShowDrawer(true);
    };

    const handleCloseDrawer = () => {
        setShowDrawer(false);
        setSelectedInquiry(null);
    };

    const handleSave = async () => {
        if (!selectedInquiry) return;

        try {
            setIsUpdating(true);
            const response = await axios.put(
                `${process.env.REACT_APP_BASE_URL}/provider-inquiry/update/${selectedInquiry._id}`,
                {
                    status: updatedStatus,
                    note: newNote.trim() || undefined,
                },
                {
                    headers: {
                        Authorization: `Bearer ${user.user?.token}`,
                    },
                }
            );

            if (response.data.success) {
                toast.success("Inquiry updated successfully");
                await fetchInquiries(); // Refresh table
                // Update selected inquiry to show new note in real-time
                setSelectedInquiry(response.data.data);
                setNewNote("");
            }
        } catch (error) {
            console.error("Error updating inquiry:", error);
            toast.error("Failed to update inquiry");
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="inquiries-section" style={{ marginTop: "60px", backgroundColor: "#fff", borderRadius: "10px", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 20px 16px" }}>
                <h2 className="app-page-title">Early Access Queue</h2>
                <div style={{ display: "flex", gap: "15px" }}>
                    <select
                        style={{
                            padding: "10px 40px 10px 15px",
                            borderRadius: "8px",
                            border: "1px solid #E0E0E0",
                            backgroundColor: "#F8F9FA",
                            fontSize: "14px",
                            color: "#495057",
                            cursor: "pointer",
                            appearance: "none",
                            WebkitAppearance: "none",
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M2 4L6 8L10 4' stroke='%23495057' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 14px center",
                            backgroundSize: "12px 12px",
                        }}
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="All">Status</option>
                        <option value="Scheduled">Scheduled</option>
                        <option value="Contacted">Contacted</option>
                        <option value="Pending">Pending</option>
                        <option value="No Response">No Response</option>
                        <option value="Not Interested">Not Interested</option>
                    </select>
                </div>
            </div>

            <div className="table-responsive">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th style={{ textAlign: "left" }}>ID</th>
                            <th style={{ textAlign: "left" }}>Name</th>
                            <th style={{ textAlign: "left" }}>State</th>
                            <th style={{ textAlign: "left" }}>Payment Preference</th>
                            <th style={{ textAlign: "left" }}>Date/Time</th>
                            <th style={{ textAlign: "center" }}>Status</th>
                            <th style={{ textAlign: "center" }}>Access</th>
                            <th style={{ textAlign: "center" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {inquiries
                            .filter(i => {
                                const matchesStatus = statusFilter === "All" || (i.status || "Pending") === statusFilter;
                                return matchesStatus;
                            })
                            .map((inquiry) => {
                                const currentStatus = inquiry.status || "Pending";
                                const colors = statusColors[currentStatus] || statusColors.Pending;

                                return (
                                    <tr key={inquiry._id}>
                                        <td style={{ fontSize: "15px", color: "#575757" }}>
                                            {inquiry.user?.userAutoGenId ? inquiry.user.userAutoGenId.toString().padStart(4, "0") : "N/A"}
                                        </td>
                                        <td style={{ fontSize: "15px", fontWeight: "500", color: "#575757" }}>{inquiry.user ? `${inquiry.user.firstname} ${inquiry.user.lastname}` : "N/A"}</td>
                                        <td style={{ fontSize: "15px", color: "#575757" }}>{inquiry.state || "N/A"}</td>
                                        <td style={{ fontSize: "15px", color: "#575757" }}>{inquiry.payment_method || "N/A"}</td>
                                        <td style={{ fontSize: "15px", color: "#575757" }}>{new Date(inquiry.createdAt).toLocaleString([], { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                                        <td style={{ textAlign: "center" }}>
                                            <span style={{
                                                padding: "6px 15px",
                                                borderRadius: "20px",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                backgroundColor: colors.bg,
                                                color: colors.color,
                                                display: "inline-block",
                                                minWidth: "100px"
                                            }}>
                                                {currentStatus}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: "15px", color: "#575757" }}>{inquiry.early_access ? "Yes" : "No"}</td>
                                        <td style={{ textAlign: "center" }}>
                                            <button
                                                className="app-view-btn"
                                                onClick={() => handleOpenDrawer(inquiry)}
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        {inquiries.length === 0 && (
                            <tr>
                                <td colSpan="8" style={{ textAlign: "center", padding: "20px", color: "#999" }}>No inquiries found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Detail Drawer Overhead */}
            {showDrawer && selectedInquiry && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        display: "flex",
                        justifyContent: "flex-end",
                        zIndex: 2000,
                    }}
                    onClick={handleCloseDrawer}
                >
                    <div
                        style={{
                            width: "500px",
                            height: "100%",
                            backgroundColor: "#fff",
                            padding: "30px",
                            boxShadow: "-5px 0 25px rgba(0,0,0,0.1)",
                            display: "flex",
                            flexDirection: "column",
                            overflowY: "auto",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
                            <h2 className="app-page-title">Early Access Queue</h2>
                            <button
                                onClick={handleCloseDrawer}
                                style={{
                                    padding: "8px 20px",
                                    backgroundColor: "#fff",
                                    border: "1px solid #E0E0E0",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "500"
                                }}
                            >
                                Close
                            </button>
                        </div>

                        {/* Status & Date */}
                        <div style={{ display: "flex", gap: "10px", marginBottom: "30px" }}>
                            <div style={{
                                padding: "8px 25px",
                                backgroundColor: "#F3F4F6",
                                borderRadius: "50px",
                                fontSize: "14px",
                                color: "#064260",
                                fontWeight: "500",
                                display: "flex",
                                alignItems: "center"
                            }}>
                                {new Date(selectedInquiry.createdAt).toLocaleString('en-US', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: 'numeric',
                                    hour12: true
                                }).replace(/,([^,]*)$/, ' ·$1')}
                            </div>
                            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                <select
                                    value={updatedStatus}
                                    onChange={(e) => setUpdatedStatus(e.target.value)}
                                    style={{
                                        padding: "8px 35px 8px 25px",
                                        backgroundColor: statusColors[updatedStatus]?.bg || "#FFFED8",
                                        color: statusColors[updatedStatus]?.color || "#687000",
                                        border: "none",
                                        borderRadius: "50px",
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        cursor: "pointer",
                                        appearance: "none",
                                        minWidth: "140px",
                                        textAlign: "center",
                                        outline: "none"
                                    }}
                                >
                                    <option value="Pending" style={{ backgroundColor: "#fff", color: "#000" }}>Pending</option>
                                    <option value="Contacted" style={{ backgroundColor: "#fff", color: "#000" }}>Contacted</option>
                                    <option value="Scheduled" style={{ backgroundColor: "#fff", color: "#000" }}>Scheduled</option>
                                    <option value="Not Interested" style={{ backgroundColor: "#fff", color: "#000" }}>Not Interested</option>
                                    <option value="No Response" style={{ backgroundColor: "#fff", color: "#000" }}>No Response</option>
                                </select>
                                <span style={{
                                    position: "absolute",
                                    right: "15px",
                                    pointerEvents: "none",
                                    color: statusColors[updatedStatus]?.color || "#687000",
                                    fontSize: "10px"
                                }}>▼</span>
                            </div>
                        </div>

                        {/* User Info Section */}
                        <div style={{ border: "1px solid #F3F4F6", borderRadius: "12px", padding: "20px", marginBottom: "25px" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                                <div>
                                    <label style={{ fontSize: "12px", color: "#9CA3AF", display: "block", marginBottom: "4px" }}>Patient</label>
                                    <div style={{ fontWeight: "600", fontSize: "15px" }}>{selectedInquiry.user?.firstname} {selectedInquiry.user?.lastname}</div>
                                    <div style={{ fontSize: "12px", color: "#9CA3AF" }}>DOB: {selectedInquiry.user?.dob ? new Date(selectedInquiry.user.dob).toLocaleDateString('en-US') : "N/A"}</div>
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", color: "#9CA3AF", display: "block", marginBottom: "4px" }}>Client ID</label>
                                    <div style={{ fontWeight: "600", fontSize: "15px" }}>
                                        {selectedInquiry.user?.userAutoGenId ? selectedInquiry.user.userAutoGenId.toString().padStart(4, "0") : "N/A"}
                                    </div>
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", color: "#9CA3AF", display: "block", marginBottom: "4px" }}>Email</label>
                                    <div style={{ fontWeight: "600", fontSize: "15px" }}>{selectedInquiry.user?.email || "N/A"}</div>
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", color: "#9CA3AF", display: "block", marginBottom: "4px" }}>Phone Number</label>
                                    <div style={{ fontWeight: "600", fontSize: "15px" }}>{selectedInquiry.user?.phoneno || "N/A"}</div>
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", color: "#9CA3AF", display: "block", marginBottom: "4px" }}>State</label>
                                    <div style={{ fontWeight: "600", fontSize: "15px" }}>{selectedInquiry.state || "N/A"}</div>
                                </div>
                            </div>
                        </div>

                        {/* Therapy Interest Section */}
                        <div style={{ border: "1px solid #F3F4F6", borderRadius: "12px", padding: "20px", marginBottom: "25px" }}>
                            <div style={{ marginBottom: "15px" }}>
                                <label style={{ fontSize: "12px", color: "#9CA3AF", display: "block", marginBottom: "4px" }}>Therapy Preference</label>
                                <div style={{ fontWeight: "600", fontSize: "15px" }}>{selectedInquiry.step1 || "N/A"}</div>
                            </div>
                            <div style={{ marginBottom: "15px" }}>
                                <label style={{ fontSize: "12px", color: "#9CA3AF", display: "block", marginBottom: "4px" }}>Urgency</label>
                                <div style={{ fontWeight: "600", fontSize: "15px" }}>{selectedInquiry.step2 || "N/A"}</div>
                            </div>
                            <div>
                                <label style={{ fontSize: "12px", color: "#9CA3AF", display: "block", marginBottom: "4px" }}>Payment Preference</label>
                                <div style={{ fontWeight: "600", fontSize: "15px" }}>{selectedInquiry.payment_method || "N/A"}</div>
                            </div>
                        </div>

                        {/* Notes History */}
                        <div style={{ marginBottom: "20px" }}>
                            <label style={{ fontWeight: "700", fontSize: "16px", display: "block", marginBottom: "15px" }}>Notes / Activity</label>
                            {selectedInquiry.notes && selectedInquiry.notes.length > 0 && (
                                <div style={{ marginBottom: "15px", maxHeight: "200px", overflowY: "auto" }}>
                                    {selectedInquiry.notes.map((note, idx) => (
                                        <div key={idx} style={{ padding: "10px", backgroundColor: "#F9FAFB", borderRadius: "8px", marginBottom: "8px", fontSize: "14px" }}>
                                            <div style={{ fontWeight: "600", marginBottom: "4px" }}>{note.text}</div>
                                            <div style={{ fontSize: "11px", color: "#9CA3AF" }}>
                                                {new Date(note.createdAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <textarea
                                placeholder={isNoteFocused ? "" : "Add internal note"}
                                onFocus={() => setIsNoteFocused(true)}
                                onBlur={() => setIsNoteFocused(false)}
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                style={{
                                    width: "100%",
                                    height: "100px",
                                    padding: "15px",
                                    borderRadius: "12px",
                                    border: "1px solid #F3F4F6",
                                    fontSize: "14px",
                                    marginBottom: "20px",
                                    resize: "none",
                                    outline: "none"
                                }}
                            />
                        </div>

                        {/* Footer Action */}
                        <div style={{ display: "flex", justifyContent: "flex-start", marginTop: "10px" }}>
                            <button
                                onClick={handleSave}
                                disabled={isUpdating}
                                style={{
                                    padding: "12px 0",
                                    width: "160px",
                                    backgroundColor: "#27B190",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    textAlign: "center"
                                }}
                            >
                                {isUpdating ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EarlyAccessQueue;

